import React, { useState } from 'react';
import { getStorePrice, getStorePriceMap } from '../../../../utils/tradeInvestment';

/** Derived rates for the analysis card. */
export function getVisitRates({ totalStores, uniqueStoresVisited, totalVisits }) {
    const visitRate = totalStores > 0 ? (uniqueStoresVisited / totalStores) * 100 : 0;
    const avgVisitsPerStore = uniqueStoresVisited > 0 ? (totalVisits / uniqueStoresVisited).toFixed(1) : 0;
    return { visitRate, avgVisitsPerStore };
}

/** Filas de "Resultados por Piso de Venta" y guardado del precio por piso. */
export function useContractStoreResults({ contract, contractChecks, onStorePriceChange }) {
    const [savingStoreId, setSavingStoreId] = React.useState(null);

    // Create a map of store checks by store ID
    const storeCheckMap = new Map();
    contractChecks?.forEach((check) => {
        storeCheckMap.set(check.storeId, check);
    });

    const priceMap = getStorePriceMap(contract);

    const handlePriceChange = async (storeId, value) => {
        if (!onStorePriceChange) return;
        const price = Number(value) || 0;
        if (price === getStorePrice(contract, storeId, priceMap)) return;
        try {
            setSavingStoreId(storeId);
            await onStorePriceChange(storeId, price);
        } finally {
            setSavingStoreId(null);
        }
    };

    // Build data for all stores in contract
    const storeData =
        contract?.stores?.map((store) => {
            const check = storeCheckMap.get(store._id);

            let imageCount = 0;
            let images = [];

            if (check) {
                if (check.images?.length) {
                    check.images.forEach((img) => {
                        if (img.url) {
                            images.push({ url: img.url });
                            imageCount++;
                        }
                    });
                }
            }

            return {
                key: store._id,
                storeId: store._id,
                storeName: store.name,
                clientName: store.clientName || 'N/A',
                storeType: store.storeType || 'N/A',
                visited: !!check,
                available: check?.available,
                imageCount,
                images,
                checkDate: check?.createdAt,
                price: getStorePrice(contract, store._id, priceMap),
                hasCustomPrice: priceMap.has(String(store._id))
            };
        }) || [];

    const totalInvestment = storeData.reduce((total, row) => total + (row.price || 0), 0);

    return { storeData, totalInvestment, savingStoreId, handlePriceChange };
}

/** Imágenes de evidencia con filtros por piso y búsqueda. */
export function useContractImageGallery({ contract, contractChecks }) {
    const [filterStore, setFilterStore] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    // Collect all images with metadata
    const allImages = [];

    contractChecks?.forEach((check) => {
        const store = contract?.stores?.find((s) => s._id === check.storeId);

        if (check.images?.length) {
            check.images.forEach((img, idx) => {
                if (img.url) {
                    allImages.push({
                        url: img.url,
                        storeId: check.storeId,
                        storeName: store?.name || 'Desconocido',
                        clientName: store?.clientName || 'N/A',
                        date: check.createdAt,
                        available: check.available,
                        key: `${check.storeId}-${idx}`
                    });
                }
            });
        }
    });

    // Get unique stores for filters
    const uniqueStores = [...new Set(allImages.map((img) => img.storeId))].map((storeId) => {
        const img = allImages.find((i) => i.storeId === storeId);
        return { id: storeId, name: img.storeName };
    });

    // Apply filters
    let filteredImages = allImages;

    if (filterStore !== 'all') {
        filteredImages = filteredImages.filter((img) => img.storeId === filterStore);
    }

    if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filteredImages = filteredImages.filter((img) => img.storeName.toLowerCase().includes(term) || img.clientName.toLowerCase().includes(term));
    }

    // Sort by date (newest first)
    filteredImages.sort((a, b) => new Date(b.date) - new Date(a.date));

    return { allImages, filteredImages, uniqueStores, filterStore, setFilterStore, searchTerm, setSearchTerm };
}
